/* eslint-disable @typescript-eslint/no-namespace */
import { z } from 'zod';
import { apiClientResp } from '../merchant/error.js';
import { claimAddress, locationAddress, orderAddress, } from '../schema/address.js';
import { calculatedAfterReasonSelection, calculatedBeforeClaimSubmission, calculatedOrder, } from '../schema/calculatedOrder.js';
import { claim, claimCreate, tag } from '../schema/claim.js';
import { calculatedOrderLineItem, claimLineItemCreate, variantExchangeLineItem, } from '../schema/claimLineItem.js';
import { customerUiEventCustomField, customUiCustomField, customUiCustomFieldSelect, } from '../schema/customField.js';
import { crewOrder, crewOrderLineItem, storeOrder } from '../schema/order.js';
import { Pegasus } from '../schema/pegasus.js';
import { registration, registrationCreate } from '../schema/registration.js';
import { shipment, shipmentCreate, shipmentQuoteBody, } from '../schema/shipment.js';
import { rate, returnShipmentMethod } from '../schema/shipmentRate.js';
import { shippingClaimCreateCustomer, shippingClaimCreateMerchant, shippingClaimCustomerUi, } from '../schema/shippingClaim/shippingClaim.js';
import { signedUrl } from '../schema/signedUrl.js';
import { registrationProduct, variant } from '../schema/storeProduct.js';
import { orderTracker } from '../schema/tracking.js';
import { orderEditCommitOrStage, orderEditStageResponse, } from './schema/orderEdit.js';
import { paymentIntent, paymentIntentCreate } from './schema/payment.js';
import { settings } from './schema/settings.js';
import { shopNowSession, shopNowSessionCreate, shopNowSessionUpdate, } from './schema/shopNow.js';
import { collection, productOptions, productVariantExchangeResponse, } from './schema/variantExchange.js';
/**
 * CrewUi types for the crew-customer-ui
 * Utility types to be used in conjunction with the crewCustomerUiApi
 */
export var CrewUi;
(function (CrewUi) {
    CrewUi.REF_QUERY_PARAM = 'ref';
    // ? urls in use by the backend API for direct linking  to the crew-customer-ui
    CrewUi.frontEndUrlPaths = [
        // SLUG branded URLS
        ':slug/claim/overview/:claimExternalId', // CREW claim overview
        ':slug/order/:idFromPlatform', // order hub
        ':slug/reorder/:idFromPlatform', // branded start a gsp reorder
        ':slug/reorder/:idFromPlatform/claim/:shippingClaimId', // shipping claim overview
        'reorder/:idFromPlatform', // start a reorder without a slug
        ':slug/register', // branded registration page
    ];
    // --------- Embedded window event ---------------------------
    CrewUi.EmbeddedWindowEvent = {
        CORSO_HEIGHT_CHANGE: 'CORSO_HEIGHT_CHANGE',
        CORSO_REQUEST_PARENT_LOCATION: 'CORSO_REQUEST_PARENT_LOCATION',
        CORSO_PARENT_LOCATION: 'CORSO_PARENT_LOCATION',
        CORSO_REQUEST_NAVIGATION: 'CORSO_REQUEST_NAVIGATION',
    };
    const heightChangeMessageSchema = z.object({
        type: z.literal(CrewUi.EmbeddedWindowEvent.CORSO_HEIGHT_CHANGE),
        height: z.number().min(0),
    });
    const requestParentLocationMessageSchema = z.object({
        type: z.literal(CrewUi.EmbeddedWindowEvent.CORSO_REQUEST_PARENT_LOCATION),
    });
    const parentLocationMessageSchema = z.object({
        type: z.literal(CrewUi.EmbeddedWindowEvent.CORSO_PARENT_LOCATION),
        location: z.string().url(),
    });
    const navigateParentMessageSchema = z.object({
        type: z.literal(CrewUi.EmbeddedWindowEvent.CORSO_REQUEST_NAVIGATION),
        url: z.string().url(),
    });
    CrewUi.embeddedMessageSchema = z.discriminatedUnion('type', [
        heightChangeMessageSchema,
        requestParentLocationMessageSchema,
        parentLocationMessageSchema,
        navigateParentMessageSchema,
    ]);
    CrewUi.variantExchangeSchema = productVariantExchangeResponse;
    CrewUi.AdvancedExchangeMethodSchema = Pegasus.advancedExchangeMethodUnion;
    CrewUi.AdvancedExchangeMethod = Pegasus.AdvancedExchangeMethod, CrewUi.orderEditAction = Pegasus.orderEditAction;
    // --------- Order Edit ---------
    CrewUi.orderEditStagedIdSchema = orderEditStageResponse;
    CrewUi.orderEditStagedSchema = orderEditCommitOrStage;
    CrewUi.OrderEditType = Pegasus.OrderEditType;
    CrewUi.orderEditActionSchema = Pegasus.orderEditAction;
    CrewUi.orderEditCancelOrderActionSchema = Pegasus.orderEditCancelOrderActionSchema, CrewUi.orderEditUpdateAddressActionSchema = Pegasus.orderEditUpdateAddressActionSchema;
    CrewUi.variantExchangeOptionsSchema = variant;
    CrewUi.variantExchangeProductOptionsSchema = productOptions;
    CrewUi.collectionSchema = collection;
    CrewUi.paymentIntentSchema = paymentIntent;
    CrewUi.paymentIntentCreateSchema = paymentIntentCreate;
    CrewUi.customFieldSelectSchema = customUiCustomFieldSelect;
    CrewUi.customFieldGeneralSchema = customUiCustomField;
    CrewUi.collectCustomFieldEventSchema = customerUiEventCustomField;
    CrewUi.claimSchema = claim;
    CrewUi.claimTagSchema = tag;
    CrewUi.variantExchangeLineItemSchema = variantExchangeLineItem;
    CrewUi.claimAddressSchema = claimAddress;
    CrewUi.claimCreateSchema = claimCreate;
    CrewUi.claimLineItemCreateSchema = claimLineItemCreate;
    CrewUi.registrationSchema = registration;
    CrewUi.registrationCreateSchema = registrationCreate;
    CrewUi.registrationProductSchema = registrationProduct;
    CrewUi.orderSchema = crewOrder;
    CrewUi.crewOrderLineItemSchema = crewOrderLineItem;
    CrewUi.orderAddressSchema = orderAddress;
    CrewUi.orderTrackerSchema = orderTracker;
    CrewUi.signedUrlSchema = signedUrl;
    CrewUi.settingsSchema = settings;
    CrewUi.shipmentQuoteSchema = shipmentQuoteBody;
    CrewUi.shipmentRateSchema = rate;
    CrewUi.shipmentMethodSchema = returnShipmentMethod;
    CrewUi.shipmentCreateSchema = shipmentCreate;
    CrewUi.shipmentSchema = shipment;
    CrewUi.locationAddressSchema = locationAddress;
    CrewUi.apiClientRespSchema = apiClientResp;
    CrewUi.shippingClaimSchema = shippingClaimCustomerUi;
    CrewUi.shippingClaimCreateCustomerSchema = shippingClaimCreateCustomer;
    CrewUi.shippingClaimCreateMerchantSchema = shippingClaimCreateMerchant;
    CrewUi.gspOrderSchema = storeOrder;
    CrewUi.calculatedOrderSchema = calculatedOrder;
    CrewUi.calculatedOrderLineItemSchema = calculatedOrderLineItem;
    CrewUi.calculatedAfterReasonSelectionSchema = calculatedAfterReasonSelection;
    CrewUi.calculatedBeforeClaimSubmissionSchema = calculatedBeforeClaimSubmission;
    CrewUi.shopNowSessionSchema = shopNowSession;
    CrewUi.shopNowSessionCreateSchema = shopNowSessionCreate;
    CrewUi.shopNowSessionUpdateSchema = shopNowSessionUpdate;
})(CrewUi || (CrewUi = {}));
//# sourceMappingURL=schema.js.map